import React from 'react';
import { useQuery } from 'react-query';
import { useFinancialPositionsStore } from '@components/Stores/FinancialPositionStore';
import { getHedgePositions, getDerivedHedges, HedgePosition } from '../services/hedgesService';
import { useAppSession } from '../contexts/AppSessionContext';
import { useBaseHedgeMaterials } from './useBaseHedgeMaterials';
import { useFormulasPrices } from './useFormulasPrices';
import { useAccountStore } from '../components/Stores/accountStore';

export const useFinancialPositions = () => {
  const { setHedgePositions } = useFinancialPositionsStore();
  const { user } = useAppSession();
  const { account } = useAccountStore();
  const accountId = user?.accountId;
  const numericAccountId = accountId ? parseInt(accountId) : undefined;

  // Check whether this account has a derivation template configured
  const { data: baseHedgeMaterials } = useBaseHedgeMaterials(numericAccountId);
  const hasDerivation = !!(baseHedgeMaterials && baseHedgeMaterials.length > 0);

  // Live prices — passed to backend so derivation uses current market prices
  const { data: formulasPricesData } = useFormulasPrices();
  const pricesMap = React.useMemo(() => {
    if (!formulasPricesData?.pricesMap) return {};
    return Object.fromEntries(
      Object.entries(formulasPricesData.pricesMap).map(([key, val]) => [key, val.price])
    );
  }, [formulasPricesData]);

  const horizonMonths = account?.viewingHorizonMonths || 24;

  const fetchFunction = async (): Promise<HedgePosition[]> => {
    if (!numericAccountId) return [];

    if (hasDerivation) {
      // Backend performs terminal-granularity derivation — single truth path
      const { positions } = await getDerivedHedges(numericAccountId, pricesMap, horizonMonths);
      console.log(`[HEDGE_DERIVATION] Received ${positions.length} derived hedges from backend`);
      return positions;
    }

    // No derivation configured — return raw hedges
    return getHedgePositions({ accountId: numericAccountId });
  };

  const query = useQuery<HedgePosition[]>(
    ['Hedges-Positions', accountId, hasDerivation, Object.keys(pricesMap).length],
    fetchFunction,
    {
      onSuccess: (data) => {
        if (data) setHedgePositions(data);
      },
      onError: (error) => {
        console.error('[useFinancialPositions] Error:', error);
      },
      enabled: !!accountId,
      staleTime: 30000,
      refetchOnWindowFocus: false,
    }
  );

  return query;
};
